// Seite „Erinnerungen" (/reminders). Vertrag: docs/API.md 3.10.
import type { ReminderDto } from '@lagerfeuer/shared';
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { remindersApi } from '../api';
import { formatDateTime, formatRelative, fromLocalInput, toLocalInput } from '../format';
import { MessagePreview } from '../message-preview';
import { atDayTime, HOUR, useNow, validateRemindAt } from '../scheduled/time';
import { toast } from '../toast';
import { Badge, Button, ConfirmButton, Empty, ErrorBox, ExtrasPage, Field, Loading, Tabs } from '../ui';
import { REMINDERS_KEY, remindersListKey } from './notify';
import { RemindMeDialog } from './RemindMeDialog';
import './reminders.css';

type Tab = 'open' | 'done';

export function RemindersPage() {
  const [tab, setTab] = useState<Tab>('open');
  const [creating, setCreating] = useState(false);
  const now = useNow();
  const done = tab === 'done';
  const list = useQuery({ queryKey: remindersListKey(done), queryFn: () => remindersApi.list({ done }) });
  const items = [...(list.data ?? [])].sort((a, b) => (done ? -1 : 1) * (new Date(a.remindAt).getTime() - new Date(b.remindAt).getTime()));

  return (
    <ExtrasPage
      title="Erinnerungen"
      testId="reminders-page"
      actions={
        <Button variant="primary" testId="reminders-new" onClick={() => setCreating(true)}>
          Neue Erinnerung
        </Button>
      }
    >
      <Tabs
        value={tab}
        onChange={setTab}
        testId="reminders-tabs"
        items={[
          { value: 'open', label: 'Offen' },
          { value: 'done', label: 'Erledigt' },
        ]}
      />
      {list.isPending ? (
        <Loading testId="reminders-loading" />
      ) : list.isError ? (
        <ErrorBox error={list.error} testId="reminders-error" />
      ) : items.length === 0 ? (
        <Empty testId="reminders-empty">{done ? 'Noch nichts erledigt.' : 'Keine offenen Erinnerungen. Lege eine an oder nutze „Erinnere mich" an einer Nachricht.'}</Empty>
      ) : (
        <ul className="lfx-remind-list" data-testid="reminders-list">
          {items.map((r) => (
            <ReminderItem key={r.id} reminder={r} now={now} />
          ))}
        </ul>
      )}
      {creating && <RemindMeDialog onClose={() => setCreating(false)} />}
    </ExtrasPage>
  );
}

function ReminderItem({ reminder: r, now }: { reminder: ReminderDto; now: number }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'view' | 'note' | 'snooze'>('view');
  const overdue = !r.done && new Date(r.remindAt).getTime() <= now;
  const update = useMutation({
    mutationFn: (body: { remindAt?: string; note?: string | null; done?: boolean }) => remindersApi.update(r.id, body),
    onSuccess: (_dto, body) => {
      void qc.invalidateQueries({ queryKey: REMINDERS_KEY });
      setMode('view');
      if (body.done !== undefined) toast({ kind: 'info', title: body.done ? 'Erinnerung erledigt' : 'Erinnerung wieder geöffnet' });
      else if (body.remindAt) toast({ kind: 'info', title: 'Erinnerung verschoben', body: formatDateTime(body.remindAt) });
    },
  });
  const remove = useMutation({
    mutationFn: () => remindersApi.remove(r.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: REMINDERS_KEY }),
  });
  const snooze = (to: Date) => update.mutate({ remindAt: to.toISOString() });

  return (
    <li
      className={`lfx-card lfx-remind-item${overdue ? ' lfx-remind-item--overdue' : ''}${r.done ? ' lfx-remind-item--done' : ''}`}
      data-testid="reminders-item"
      data-id={r.id}
      data-overdue={overdue ? 'true' : 'false'}
    >
      <div className="lfx-row lfx-row--between">
        <div className="lfx-row">
          <time dateTime={r.remindAt} className="lfx-remind-due" data-testid="reminders-item-due">
            {formatRelative(r.remindAt, now)}
          </time>
          <span className="lfx-muted">{formatDateTime(r.remindAt)}</span>
          {overdue && (
            <Badge tone="danger" testId="reminders-item-overdue">
              überfällig
            </Badge>
          )}
          {r.done && r.doneAt && <span className="lfx-muted">erledigt {formatDateTime(r.doneAt)}</span>}
        </div>
      </div>

      {mode === 'note' ? (
        <NoteForm initial={r.note ?? ''} pending={update.isPending} onCancel={() => setMode('view')} onSave={(note) => update.mutate({ note })} />
      ) : (
        r.note && (
          <p className="lfx-remind-note" data-testid="reminders-item-note">
            {r.note}
          </p>
        )
      )}

      {r.message ? (
        <MessagePreview message={r.message} testId="reminders-item-message" />
      ) : r.messageId ? (
        <div className="lfx-muted" data-testid="reminders-item-message-missing">
          Nachricht nicht mehr verfügbar.
        </div>
      ) : null}

      {mode === 'snooze' && <SnoozeForm initial={r.remindAt} pending={update.isPending} onCancel={() => setMode('view')} onSave={(iso) => update.mutate({ remindAt: iso })} />}

      <ErrorBox error={update.error ?? remove.error} testId="reminders-item-error" />

      {mode === 'view' && (
        <div className="lfx-row">
          {r.done ? (
            <Button testId="reminders-item-reopen" disabled={update.isPending} onClick={() => update.mutate({ done: false })}>
              Wieder öffnen
            </Button>
          ) : (
            <>
              <Button variant="primary" testId="reminders-item-done" disabled={update.isPending} onClick={() => update.mutate({ done: true })}>
                Erledigt
              </Button>
              <Button testId="reminders-item-snooze-1h" disabled={update.isPending} onClick={() => snooze(new Date(Math.max(now, new Date(r.remindAt).getTime()) + HOUR))}>
                +1 h
              </Button>
              <Button testId="reminders-item-snooze-tomorrow" disabled={update.isPending} onClick={() => snooze(atDayTime(1, 9))}>
                Morgen 9:00
              </Button>
              <Button variant="ghost" testId="reminders-item-snooze" onClick={() => setMode('snooze')}>
                Verschieben …
              </Button>
            </>
          )}
          <Button variant="ghost" testId="reminders-item-edit-note" onClick={() => setMode('note')}>
            {r.note ? 'Notiz bearbeiten' : 'Notiz hinzufügen'}
          </Button>
          <ConfirmButton label="Löschen" confirmLabel="Wirklich löschen?" testId="reminders-item-delete" disabled={remove.isPending} onConfirm={() => remove.mutate()} />
        </div>
      )}
    </li>
  );
}

function NoteForm(props: { initial: string; pending: boolean; onSave: (note: string | null) => void; onCancel: () => void }) {
  const max = FIXED_LIMITS.reminderNoteMax;
  const [note, setNote] = useState(props.initial);
  const tooLong = note.length > max;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (tooLong) return;
    props.onSave(note.trim() ? note.trim() : null);
  };
  return (
    <form className="lfx-stack" onSubmit={submit} data-testid="reminders-note-form">
      <Field label="Notiz" hint={<span className={tooLong ? 'lfx-remind-count lfx-remind-count--over' : 'lfx-remind-count'}>{`${note.length} / ${max}`}</span>} error={tooLong ? `Höchstens ${max} Zeichen.` : null}>
        {(id) => <textarea id={id} className="lfx-textarea" data-testid="reminders-note-input" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      <div className="lfx-row">
        <Button variant="primary" type="submit" testId="reminders-note-save" disabled={props.pending || tooLong}>
          Speichern
        </Button>
        <Button testId="reminders-note-cancel" onClick={props.onCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function SnoozeForm(props: { initial: string; pending: boolean; onSave: (iso: string) => void; onCancel: () => void }) {
  const [when, setWhen] = useState(() => toLocalInput(props.initial));
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const iso = fromLocalInput(when);
    const err = validateRemindAt(iso);
    setError(err);
    if (!err && iso) props.onSave(iso);
  };
  return (
    <form className="lfx-stack" onSubmit={submit} data-testid="reminders-snooze-form" noValidate>
      <Field label="Neuer Zeitpunkt" error={error}>
        {(id) => <input id={id} type="datetime-local" className="lfx-input" data-testid="reminders-snooze-when" value={when} onChange={(e) => setWhen(e.target.value)} />}
      </Field>
      <div className="lfx-row">
        <Button variant="primary" type="submit" testId="reminders-snooze-save" disabled={props.pending}>
          Verschieben
        </Button>
        <Button testId="reminders-snooze-cancel" onClick={props.onCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}
